import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar as CalendarIcon, Download } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const { selectedProperty } = useOutletContext();
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [arrivals, setArrivals] = useState([]);
  const [departures, setDepartures] = useState([]);
  const [occupancy, setOccupancy] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedProperty) {
      fetchReports();
    }
  }, [selectedProperty, date]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const [arrivalsRes, departuresRes, occupancyRes] = await Promise.all([
        axios.get(`${API}/reports/arrivals?property_id=${selectedProperty.id}&date=${date}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reports/departures?property_id=${selectedProperty.id}&date=${date}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reports/occupancy?property_id=${selectedProperty.id}&date=${date}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setArrivals(arrivalsRes.data);
      setDepartures(departuresRes.data);
      setOccupancy(occupancyRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-600 mt-1">Daily operational reports</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <CalendarIcon size={18} className="text-slate-500" />
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-auto"
              data-testid="date-picker"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading reports...</div>
      ) : (
        <>
          {/* Occupancy Summary */}
          <Card className="border-l-4 border-l-cyan-500" data-testid="occupancy-summary">
            <CardHeader>
              <CardTitle>Occupancy Summary</CardTitle>
            </CardHeader>
            <CardContent>
              {occupancy && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Occupancy Rate</p>
                    <p className="text-3xl font-bold text-slate-900">{occupancy.occupancy_rate}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Occupied Rooms</p>
                    <p className="text-3xl font-bold text-cyan-600">{occupancy.occupied_units}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Rooms</p>
                    <p className="text-3xl font-bold text-slate-900">{occupancy.total_units}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Arrivals Report */}
          <Card data-testid="arrivals-report">
            <CardHeader>
              <CardTitle>Arrivals ({arrivals.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {arrivals.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No arrivals for this date</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Guest Name</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Room</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Nights</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Amount</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {arrivals.map((arrival) => (
                        <tr key={arrival.id} className="border-b border-slate-100" data-testid={`arrival-row-${arrival.id}`}>
                          <td className="p-3 text-slate-900">{arrival.guest?.name}</td>
                          <td className="p-3 text-slate-700">{arrival.unit_id || 'TBA'}</td>
                          <td className="p-3 text-slate-700">
                            {Math.ceil((new Date(arrival.check_out) - new Date(arrival.check_in)) / (1000 * 60 * 60 * 24))}
                          </td>
                          <td className="p-3 font-medium text-slate-900">${arrival.base_amount + arrival.tax_amount}</td>
                          <td className="p-3 text-slate-700">{arrival.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Departures Report */}
          <Card data-testid="departures-report">
            <CardHeader>
              <CardTitle>Departures ({departures.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {departures.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No departures for this date</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Guest Name</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Room</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Nights</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Amount</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departures.map((departure) => (
                        <tr key={departure.id} className="border-b border-slate-100" data-testid={`departure-row-${departure.id}`}>
                          <td className="p-3 text-slate-900">{departure.guest?.name}</td>
                          <td className="p-3 text-slate-700">{departure.unit_id || 'TBA'}</td>
                          <td className="p-3 text-slate-700">
                            {Math.ceil((new Date(departure.check_out) - new Date(departure.check_in)) / (1000 * 60 * 60 * 24))}
                          </td>
                          <td className="p-3 font-medium text-slate-900">${departure.base_amount + departure.tax_amount}</td>
                          <td className="p-3 text-slate-700">{departure.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default Reports;
